
import React from 'react';
import ReactDOM from 'react-dom';
import useStockRecommendModel from './StockRecommendedModel';
import Navigation from '../components/Navigation/Navigation';
import UpstoxTokenStatus from '../components/UpstoxTokenStatus';
import StockChart from '../StocksChart/stockChart';
import { motion, AnimatePresence } from 'framer-motion';

// Move SIGNAL_TYPES outside the component to avoid re-creation on every render
const SIGNAL_TYPES = {
    ALL: 'ALL',
    BUY: 'BUY',
    SELL: 'SELL',
    NEUTRAL: 'NEUTRAL'
};

// Function to play a beep sound for 15 seconds
const playBeep = () => {
    // Plays the sound file located in the public folder.
    const audio = new Audio('/siren-alert-96052.mp3');
    audio.play().catch(error => {
        // Autoplay can be blocked by the browser, log errors to the console.
        console.error("Failed to play audio alert:", error);
    });
};

export default function StockRecommend() {
    const model = useStockRecommendModel();
    const [isAnalyzing, setIsAnalyzing] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [progress, setProgress] = React.useState(0); // Loader progress 0-100
    const [signalFilter, setSignalFilter] = React.useState(SIGNAL_TYPES.ALL);
    const [showSignalFilter, setShowSignalFilter] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const mobileFilterBtnRef = React.useRef(null);
    const desktopFilterBtnRef = React.useRef(null);
    const [dropdownPos, setDropdownPos] = React.useState({ top: 0, left: 0, width: 0 });
    const chartModalRef = React.useRef(null);
    const [currentPage, setCurrentPage] = React.useState(1);
    const stocksPerPage = 100;
    const isInitialAutoAnalysis = React.useRef(true);
    const analysisLock = React.useRef(false);

    // Use real progress from fetchRecommendations
    const runWithProgress = React.useCallback(async (fn) => {
        setProgress(0);
        await fn((percent) => setProgress(percent));
        setProgress(100);
        setTimeout(() => setProgress(0), 600);
    }, []);

    // Filtered recommendations based on search and signal
    const filteredRecommendations = React.useMemo(() => {
        return model.state.recommendations.filter(stock => {
            const matchesSignal = signalFilter === SIGNAL_TYPES.ALL || stock.signal === signalFilter;
            const matchesSearch = stock.symbol.toLowerCase().includes(searchTerm.trim().toLowerCase());
            return matchesSignal && matchesSearch;
        });
    }, [model.state.recommendations, signalFilter, searchTerm]);

    // Paginated recommendations
    const paginatedRecommendations = React.useMemo(() => {
        const startIndex = (currentPage - 1) * stocksPerPage;
        const endIndex = startIndex + stocksPerPage;
        return filteredRecommendations.slice(startIndex, endIndex);
    }, [filteredRecommendations, currentPage]);

    const totalPages = Math.ceil(filteredRecommendations.length / stocksPerPage);

    const handleFilterChange = (newFilter) => {
        setSignalFilter(newFilter);
        setShowSignalFilter(false);
    };

    // Calculate dropdown position when opening
    React.useEffect(() => {
        if (showSignalFilter) {
            const activeBtn = (mobileFilterBtnRef.current && mobileFilterBtnRef.current.offsetWidth > 0)
                ? mobileFilterBtnRef.current
                : desktopFilterBtnRef.current;
                
            if (activeBtn) {
                const rect = activeBtn.getBoundingClientRect();
                const dropdownWidth = 224; // w-56
                
                // Align left relative to the right edge of the button, capped to not go off screen on mobile
                const leftPos = Math.max(16, Math.min(window.innerWidth - dropdownWidth - 16, rect.right - dropdownWidth));
                
                setDropdownPos({
                    top: rect.bottom + 8,
                    left: leftPos,
                    width: rect.width
                });
            }
        }
    }, [showSignalFilter]);

    // Monitor filter/search changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [signalFilter, searchTerm]);

    const handleAnalyze = async (isTimerTriggered = false) => {
        // Use a ref-based lock to prevent concurrent executions
        if (analysisLock.current) {
            return;
        }

        try {
            analysisLock.current = true;
            setIsAnalyzing(true);

            await runWithProgress(async (onProgress) => {
                await model.fetchRecommendations(onProgress);
            });

            if (isTimerTriggered) {
                if (isInitialAutoAnalysis.current) {
                    // Don't beep on the first automatic run on page load
                    isInitialAutoAnalysis.current = false;
                } else {
                    playBeep();
                }
            }
        } finally {
            // Ensure lock is released and state is reset
            analysisLock.current = false;
            setIsAnalyzing(false);
        }
    };

    // Create a ref to hold the latest version of the handleAnalyze function
    const handleAnalyzeRef = React.useRef(handleAnalyze);
    handleAnalyzeRef.current = handleAnalyze;




    const handleLoadSaved = async () => {
        if (analysisLock.current) return;
        setIsLoading(true);
        setProgress(0);
        await model.loadSavedAnalysis((percent) => setProgress(percent));
        setProgress(100);
        setTimeout(() => setProgress(0), 600);
        setIsLoading(false);
    };

    const handleViewData = (stock) => {
        model.viewStockData(stock);
    };

    const handleCloseChart = () => {
        model.clearSelectedStock();
    };

    const currentStockIndex = React.useMemo(() => {
        if (!model.state.selectedStock) return -1;
        return filteredRecommendations.findIndex(
            (stock) => stock.symbol === model.state.selectedStock.symbol
        );
    }, [model.state.selectedStock, filteredRecommendations]);

    const handlePreviousStock = React.useCallback(() => {
        if (currentStockIndex > 0) {
            model.viewStockData(filteredRecommendations[currentStockIndex - 1]);
        }
    }, [currentStockIndex, filteredRecommendations, model]);

    const handleNextStock = React.useCallback(() => {
        if (currentStockIndex < filteredRecommendations.length - 1) {
            model.viewStockData(filteredRecommendations[currentStockIndex + 1]);
        }
    }, [currentStockIndex, filteredRecommendations, model]);

    // Keyboard navigation for stock chart modal
    React.useEffect(() => {
        const handleKeyDown = (event) => {
            const scrollAmount = 50; // pixels to scroll
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                handlePreviousStock();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                handleNextStock();
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (chartModalRef.current) chartModalRef.current.scrollTop -= scrollAmount;
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (chartModalRef.current) chartModalRef.current.scrollTop += scrollAmount;
            }
        };

        if (model.state.selectedStock) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [model.state.selectedStock, handlePreviousStock, handleNextStock]);

    // Close filter dropdown when clicking outside (portal-aware)
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (!showSignalFilter) return;
            if (mobileFilterBtnRef.current && mobileFilterBtnRef.current.contains(event.target)) return;
            if (desktopFilterBtnRef.current && desktopFilterBtnRef.current.contains(event.target)) return;
            const dropdown = document.getElementById('signal-filter-dropdown');
            if (dropdown && dropdown.contains(event.target)) return;
            setShowSignalFilter(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showSignalFilter]);

    const Pagination = () => {
        if (totalPages <= 1) return null;

        return (
            <div className="flex justify-center items-center space-x-2 mt-4">
                <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Previous
                </button>
                <span className="text-gray-400">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-lg bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next
                </button>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#0B1121] text-gray-100">
            {/* Loader Overlay */}
            {(isAnalyzing || isLoading) && progress > 0 && progress < 100 && (
                <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="w-full max-w-xs bg-gray-900 rounded-xl p-6 shadow-2xl flex flex-col items-center">
                        <span className="text-lg font-semibold text-blue-300 mb-4">
                            {isAnalyzing ? "Analyzing Stocks..." : "Loading Analysis..."}
                        </span>
                        <div className="w-full h-4 bg-gray-800 rounded-full overflow-hidden mb-2">
                            <div
                                className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-200"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <span className="text-blue-400 font-mono text-sm">{Math.floor(progress)}%</span>
                    </div>
                </div>
            )}
            <Navigation />
            <div className="p-4 md:p-8">
                <UpstoxTokenStatus />
                <div className="max-w-5xl mx-auto space-y-8">
                    {/* Chart Section */}
                    <AnimatePresence>
                        {model.state.selectedStock && (
                            <div
                                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
                                onClick={handleCloseChart}
                            >
                                <motion.div
                                    ref={chartModalRef}
                                    className="bg-gray-800/40 rounded-2xl overflow-hidden backdrop-blur-xl border border-gray-700/50 shadow-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="p-8 relative">
                                        <div className="flex justify-between items-center mb-6">
                                            <h2 className="text-xl font-semibold text-gray-100">
                                                {model.state.selectedStock.symbol} Chart
                                            </h2>
                                            <button
                                                onClick={handleCloseChart}
                                                className="text-gray-400 hover:text-gray-200 z-10"
                                            >
                                                Close
                                            </button>
                                        </div>
                                        <StockChart symbol={model.state.selectedStock.symbol} name={model.state.selectedStock.name} />
                                    </div>
                                    {/* Navigation buttons */}
                                    <button
                                        onClick={handlePreviousStock}
                                        disabled={currentStockIndex <= 0}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/70 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                                    >
                                        <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={handleNextStock}
                                        disabled={currentStockIndex >= filteredRecommendations.length - 1}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/70 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                                    >
                                        <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </motion.div>
                            </div>
                        )}
                    </AnimatePresence>

                    {/* Recommendations Table */}
                    <motion.div
                        className={`bg-gray-800/40 rounded-2xl p-6 backdrop-blur-xl border border-gray-700/50 shadow-xl ring-1 ring-gray-700/50 overflow-hidden ${model.state.recommendations.length === 0 ? 'min-h-[320px] flex flex-col justify-center' : ''}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <div className="text-center mb-6">
                            <img src="/My_Logo_2.png" alt="StockAdi Logo" className="h-24 w-24 rounded-full object-cover mx-auto mb-4" />
                        </div>
                        <div className="flex flex-col space-y-4 mb-6">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-xl font-semibold text-gray-100">Stock Recommendations</h2>
                                    {model.state.isLoading && (
                                        <div className="text-gray-400 text-sm">Analyzing stocks...</div>
                                    )}
                                </div>
                                {model.state.error && (
                                    <div className="text-sm text-red-400 bg-red-500/10 p-3 rounded-lg">
                                        {model.state.error}
                                    </div>
                                )}
                                {model.state.metadata && (
                                    <div className="text-sm text-gray-400 grid grid-cols-2 md:grid-cols-3 gap-2">
                                        <span className="inline-flex items-center gap-1 col-span-2 md:col-span-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                            Retrieved At: {new Date(model.state.analysisTimestamp).toLocaleString()}
                                        </span>
                                    </div>
                                )}
                                {model.state.analysisTimestamp && !model.state.metadata && (
                                    <div className="text-sm text-gray-400">
                                        <span className="inline-flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Analysis from: {new Date(model.state.analysisTimestamp).toLocaleString()}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <motion.button
                                    onClick={() => handleAnalyze(false)}
                                    disabled={isAnalyzing || isLoading}
                                    className={`w-full px-6 py-3 rounded-xl font-semibold transition-all ${isAnalyzing || isLoading
                                        ? 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-purple-500/25'
                                        }`}
                                    whileHover={!isAnalyzing ? { scale: 1.02 } : {}}
                                    whileTap={!isAnalyzing ? { scale: 0.98 } : {}}
                                >
                                    {isAnalyzing ? (
                                        <div className="flex items-center justify-center gap-3">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            <span>Analyzing Top Stocks...</span>
                                        </div>
                                    ) : (
                                        'Analyze Stocks for Buy Signals'
                                    )}
                                </motion.button>

                                <motion.button
                                    onClick={handleLoadSaved}
                                    disabled={isAnalyzing || isLoading}
                                    className={`w-full px-6 py-3 rounded-xl font-semibold transition-all ${isAnalyzing || isLoading
                                        ? 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-600 text-white shadow-lg hover:shadow-teal-500/25'
                                        }`}
                                    whileHover={!isLoading ? { scale: 1.02 } : {}}
                                    whileTap={!isLoading ? { scale: 0.98 } : {}}
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center gap-3">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            <span>Loading Analysis...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center gap-2">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3zm0 5h16" />
                                            </svg>
                                            <span>Load Saved Analysis</span>
                                        </div>
                                    )}
                                </motion.button>
                            </div>
                            {/* Search Bar */}
                            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <input
                                    type="text"
                                    className="w-full max-w-md px-4 py-2 rounded-lg bg-gray-900/80 border border-gray-700 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                    placeholder="Search stock symbol..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value.toUpperCase())}
                                    autoComplete="off"
                                />
                                <div className="lg:hidden flex items-center justify-between w-full sm:w-auto gap-2">
                                    <span className="text-xs text-gray-400">Filter: {signalFilter}</span>
                                    <button
                                        ref={mobileFilterBtnRef}
                                        onClick={() => setShowSignalFilter(!showSignalFilter)}
                                        className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold transition-all ${signalFilter !== SIGNAL_TYPES.ALL
                                            ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-200'
                                            : 'bg-gray-700/70 hover:bg-gray-700/80 text-gray-100'
                                            }`}
                                    >
                                        {signalFilter === SIGNAL_TYPES.ALL ? 'Filter Signals' : signalFilter}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Main Table */}
                        <div className="overflow-x-auto relative rounded-xl">
                            {/* Mobile View */}
                            <div className="lg:hidden space-y-4">
                                {paginatedRecommendations
                                    .map((stock, index) => (
                                        <motion.div
                                            key={stock.symbol}
                                            className="bg-gray-800/30 p-4 rounded-lg border border-gray-700/50"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: index * 0.05 }}
                                        >
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-100">{stock.symbol}</h3>
                                                    <p className="text-gray-400">{stock.currentPrice != null ? `₹${Number(stock.currentPrice).toFixed(2)}` : '—'}</p>
                                                </div>
                                                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${stock.signal === 'BUY'
                                                    ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/30'
                                                    : stock.signal === 'SELL'
                                                        ? 'bg-red-500/20 text-red-400 ring-1 ring-red-500/30'
                                                        : 'bg-gray-500/20 text-gray-400 ring-1 ring-gray-500/30'
                                                    }`}>
                                                    {stock.signal === 'BUY' && (
                                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                        </svg>
                                                    )}
                                                    {stock.signal === 'SELL' && (
                                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                        </svg>
                                                    )}
                                                    {stock.signal}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <p className="text-xs text-gray-400 mb-1">Buy Range</p>
                                                    <p className="text-sm text-gray-100">
                                                        {stock.predictedBuyRange?.min != null && stock.predictedBuyRange?.max != null
                                                            ? `₹${stock.predictedBuyRange.min.toFixed(2)} - ₹${stock.predictedBuyRange.max.toFixed(2)}`
                                                            : 'N/A'}
                                                    </p>
                                                </div>

                                                <div>
                                                    <p className="text-xs text-gray-400 mb-1">Sell Range</p>
                                                    <p className="text-sm text-gray-100">
                                                        {stock.predictedSellRange?.min != null && stock.predictedSellRange?.max != null
                                                            ? `₹${stock.predictedSellRange.min.toFixed(2)} - ₹${stock.predictedSellRange.max.toFixed(2)}`
                                                            : 'N/A'}
                                                    </p>
                                                </div>

                                            </div>
                                            <button
                                                onClick={() => handleViewData(stock)}
                                                className={`w-full inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${model.state.selectedStock?.symbol === stock.symbol
                                                    ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/30'
                                                    : 'text-blue-400 hover:bg-blue-500/10 hover:text-blue-300'
                                                    }`}
                                            >
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                                </svg>
                                                View Chart
                                            </button>
                                        </motion.div>
                                    ))}
                            </div>

                            {/* Desktop View */}
                            <table className="min-w-full divide-y divide-gray-700/50 hidden lg:table">
                                <thead className="bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-blue-500/50 after:via-purple-500/50 after:to-pink-500/50">
                                    <tr>
                                        <th className="px-6 py-5 first:pl-8 last:pr-8">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-xs font-bold text-blue-300 uppercase tracking-wider whitespace-nowrap">
                                                    Stock Name
                                                </span>
                                            </div>
                                        </th>
                                        <th className="px-6 py-5 first:pl-8 last:pr-8">
                                            <div className="flex items-center justify-end space-x-2">
                                                <span className="text-xs font-bold text-blue-300 uppercase tracking-wider whitespace-nowrap">
                                                    Current Price
                                                </span>
                                            </div>
                                        </th>
                                        <th className="px-6 py-5 first:pl-8 last:pr-8">
                                            <div className="flex items-center justify-center space-x-2 relative">
                                                <span className="text-xs font-bold text-blue-300 uppercase tracking-wider whitespace-nowrap">
                                                    Signal
                                                </span>
                                                <div className="relative">
                                                    <button
                                                        ref={desktopFilterBtnRef}
                                                        onClick={() => setShowSignalFilter(!showSignalFilter)}
                                                        className={`ml-2 p-1 rounded-md transition-all duration-200 ${signalFilter !== 'ALL'
                                                            ? 'bg-purple-500/20 hover:bg-purple-500/30'
                                                            : 'hover:bg-gray-700/30'
                                                            }`}
                                                    >
                                                        <div className="relative">
                                                            <svg
                                                                className={`w-4 h-4 transition-transform duration-200 ${signalFilter !== 'ALL' ? 'text-purple-400' : 'text-blue-300'
                                                                    }`}
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                                            </svg>
                                                            {signalFilter !== 'ALL' && (
                                                                <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full"></div>
                                                            )}
                                                        </div>
                                                    </button>
                                                    {showSignalFilter && ReactDOM.createPortal(
                                                        <div
                                                            id="signal-filter-dropdown"
                                                            className="signal-filter fixed w-56 min-h-[120px] max-h-72 bg-gray-800 rounded-lg shadow-2xl z-[9999] border border-gray-700 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
                                                            style={{
                                                                top: dropdownPos.top,
                                                                left: dropdownPos.left,
                                                                pointerEvents: 'auto'
                                                            }}
                                                        >
                                                            <div className="flex flex-col h-full">
                                                                <div className="py-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleFilterChange(SIGNAL_TYPES.ALL)}
                                                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700/50 transition-colors duration-200 flex items-center space-x-2 ${signalFilter === SIGNAL_TYPES.ALL ? 'bg-purple-500/10 text-purple-400' : 'text-gray-300'}`}
                                                                    >
                                                                        <span>All Signals</span>
                                                                        {signalFilter === SIGNAL_TYPES.ALL && (
                                                                            <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleFilterChange(SIGNAL_TYPES.BUY)}
                                                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700/50 transition-colors duration-200 flex items-center space-x-2 ${signalFilter === SIGNAL_TYPES.BUY ? 'bg-purple-500/10 text-purple-400' : 'text-gray-300'}`}
                                                                    >
                                                                        <span>Buy Signals</span>
                                                                        {signalFilter === SIGNAL_TYPES.BUY && (
                                                                            <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleFilterChange(SIGNAL_TYPES.SELL)}
                                                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700/50 transition-colors duration-200 flex items-center space-x-2 ${signalFilter === SIGNAL_TYPES.SELL ? 'bg-purple-500/10 text-purple-400' : 'text-gray-300'}`}
                                                                    >
                                                                        <span>Sell Signals</span>
                                                                        {signalFilter === SIGNAL_TYPES.SELL && (
                                                                            <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleFilterChange(SIGNAL_TYPES.NEUTRAL)}
                                                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700/50 transition-colors duration-200 flex items-center space-x-2 ${signalFilter === SIGNAL_TYPES.NEUTRAL ? 'bg-purple-500/10 text-purple-400' : 'text-gray-300'}`}
                                                                    >
                                                                        <span>Neutral Signals</span>
                                                                        {signalFilter === SIGNAL_TYPES.NEUTRAL && (
                                                                            <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                                <div className="mt-auto px-4 py-2 border-t border-gray-700 bg-gray-800/50">
                                                                    <div className="text-xs text-gray-400">
                                                                        {model.state.recommendations.filter(stock => signalFilter === SIGNAL_TYPES.ALL || stock.signal === signalFilter).length} results
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>,
                                                        document.body
                                                    )}
                                                </div>
                                            </div>
                                        </th>
                                        <th className="px-6 py-5 first:pl-8 last:pr-8">
                                            <div className="flex items-center justify-center space-x-2">
                                                <span className="text-xs font-bold text-blue-300 uppercase tracking-wider whitespace-nowrap">
                                                    Historical Data
                                                </span>
                                            </div>
                                        </th>
                                        <th className="px-6 py-5 first:pl-8 last:pr-8">
                                            <div className="flex items-center justify-end space-x-2">
                                                <span className="text-xs font-bold text-blue-300 uppercase tracking-wider whitespace-nowrap">
                                                    Buy Range
                                                </span>
                                            </div>
                                        </th>
                                        <th className="px-6 py-5 first:pl-8 last:pr-8">
                                            <div className="flex items-center justify-end space-x-2">
                                                <span className="text-xs font-bold text-blue-300 uppercase tracking-wider whitespace-nowrap">
                                                    Sell Range
                                                </span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700">
                                    {paginatedRecommendations.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                                                {isAnalyzing || isLoading
                                                    ? "Analyzing stocks..."
                                                    : "Click the button above to analyze stocks and find buy signals"}
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedRecommendations
                                            .map((stock, index) =>
                                                <motion.tr
                                                    key={stock.symbol}
                                                    className={`transition-colors duration-200 hover:bg-gray-700/30 ${index % 2 === 0 ? 'bg-gray-900/20' : 'bg-gray-800/20'
                                                        }`}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-100">{stock.symbol}</div>
                                                        <div className="text-sm text-gray-400">{stock.name}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                                        <div className="text-sm font-medium text-gray-100">{stock.currentPrice != null ? `₹${Number(stock.currentPrice).toFixed(2)}` : '—'}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 ${stock.signal === 'BUY'
                                                            ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/30'
                                                            : stock.signal === 'SELL'
                                                                ? 'bg-red-500/20 text-red-400 ring-1 ring-red-500/30'
                                                                : 'bg-gray-500/20 text-gray-400 ring-1 ring-gray-500/30'
                                                            }`}>
                                                            {stock.signal === 'BUY' && (
                                                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                                </svg>
                                                            )}
                                                            {stock.signal === 'SELL' && (
                                                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                                </svg>
                                                            )}
                                                            {stock.signal}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <button
                                                            onClick={() => handleViewData(stock)}
                                                            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${model.state.selectedStock?.symbol === stock.symbol
                                                                ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/30'
                                                                : 'text-blue-400 hover:bg-blue-500/10 hover:text-blue-300'
                                                                }`}
                                                        >
                                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                                            </svg>
                                                            View Chart
                                                        </button>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                                        <div className="text-sm text-gray-100">
                                                            {stock.predictedBuyRange
                                                                && stock.predictedBuyRange.min != null
                                                                && stock.predictedBuyRange.max != null
                                                                ? (
                                                                    <span className="inline-flex items-center">
                                                                        <svg className="w-3 h-3 mr-1 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                                        </svg>
                                                                        ₹{stock.predictedBuyRange.min.toFixed(2)} - ₹{stock.predictedBuyRange.max.toFixed(2)}
                                                                    </span>
                                                                )
                                                                : <span className="text-gray-500">N/A</span>
                                                            }

                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                                        <div className="text-sm text-gray-100">
                                                            {stock.predictedSellRange
                                                                && stock.predictedSellRange.min != null
                                                                && stock.predictedSellRange.max != null
                                                                ? (
                                                                    <span className="inline-flex items-center">
                                                                        <svg className="w-3 h-3 mr-1 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                                        </svg>
                                                                        ₹{stock.predictedSellRange.min.toFixed(2)} - ₹{stock.predictedSellRange.max.toFixed(2)}
                                                                    </span>
                                                                )
                                                                : <span className="text-gray-500">N/A</span>
                                                            }
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            ))}
                                </tbody>
                            </table>
                            <Pagination />
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
